import { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import { Link } from "@tanstack/react-router";
import { CloudOffIcon, MenuIcon, PowerIcon } from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import useNetworkStatus from "~/hooks/use-network-status";
import { useEmployeeManager } from "~/managers/use-employee-manager";
import { useAuthStore } from "~/stores/use-auth-store";
import { useGeneralStore } from "~/stores/use-general-store";
import { usePermissionStore } from "~/stores/use-permission-store";
import { usePreferencesStore } from "~/stores/use-preferences-store";
import { Clock } from "../miscellaneous/clock";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import Notifications from "./notifications";

export function Header() {
  const { mobileNavExpanded, setMobileNavExpanded } = useGeneralStore();
  const { openProfile } = useEmployeeManager();
  const { currentEmployee } = usePermissionStore();
  const { homePage } = usePreferencesStore();
  const isOnline = useNetworkStatus();
  const [networkStatusOpen, setNetworkStatusOpen] = useState(false);
  const { impersonateId, setImpersonateId } = useAuthStore(
    useShallow((state) => ({
      impersonateId: state.impersonateId,
      setImpersonateId: state.setImpersonateId,
    })),
  );

  useEffect(() => {
    if (!isOnline) {
      setNetworkStatusOpen(true);
    } else {
      setNetworkStatusOpen(false);
    }
  }, [isOnline]);

  return (
    <header className="flex items-center border-b shadow-sm h-14 justify-between relative bg-background-secondary">
      <div className="flex flex-row items-center h-full gap-2">
        <Link
          href={homePage}
          className="h-full w-16 bg-black relative flex items-center justify-center border-r"
        >
          <img
            alt="App Icon"
            src="/app-icon-192.png"
            className="h-full p-1 object-contain"
          />
        </Link>

        <Button
          onClick={() => {
            setMobileNavExpanded(!mobileNavExpanded);
          }}
          className="p-2 md:hidden"
          variant="ghost"
        >
          {mobileNavExpanded ? <CloseIcon /> : <MenuIcon />}
        </Button>
      </div>
      <div className="md:flex hidden">
        <Clock />
      </div>
      <div className="flex flex-row items-center gap-2 pr-2 py-2">
        {impersonateId && (
          <Button
            onClick={() => {
              setImpersonateId(null);
            }}
            className="p-2"
            variant="ghost"
          >
            <PowerIcon />
          </Button>
        )}
        {!isOnline && (
          <Button className="p-2" variant="ghost">
            <CloudOffIcon />
          </Button>
        )}
        <Notifications />

        <Button
          onClick={openProfile}
          className="p-2"
          variant={currentEmployee ? "success" : "ghost"}
        >
          <PersonIcon />
        </Button>
      </div>
      <Dialog open={networkStatusOpen} onOpenChange={setNetworkStatusOpen}>
        <DialogContent className="max-w-lg w-full">
          <DialogHeader>
            <DialogTitle>No server connection</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[80vh] p-4">
            <p className="text-center">
              You are not connected to the server. All data will be stored
              locally. When your connection is back, it will be synced with the
              server.
            </p>
          </ScrollArea>
          <DialogFooter>
            <DialogClose asChild>
              <Button>Ok</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
}
